import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import { parseLocalDateTime } from "src/utils/parseLocalDateTime";
import {
  generatedApi,
  GetStrategyDashboardHeatsApiArg,
  useGetStrategyDashboardHeatsQuery,
} from "src/store/api/generatedApi";
import { useAppDispatch } from "src/store/store";
import { useTenant } from "./settings";

export interface TimeRange {
  start: string;
  end: string;
}

export const dateToMidnightRange = (
  date: Dayjs,
  timezone: string,
  startHourOffset = 0
) => {
  const calendarStart = dayjs
    .utc(date.format("YYYY-MM-DD"))
    .add(startHourOffset, "hour");
  const toTenantBoundary = (calendarTime: Dayjs): string =>
    parseLocalDateTime(
      calendarTime.format("YYYY-MM-DDTHH:mm:ss"),
      timezone
    ).toISOString();
  return {
    start: toTenantBoundary(calendarStart),
    end: toTenantBoundary(calendarStart.add(1, "day")),
  };
};

interface UseHeatsForTimeRangeOptions {
  timeRange: TimeRange;
  day?: string;
  prefetchRanges?: TimeRange[];
  filterMode?: GetStrategyDashboardHeatsApiArg["filterMode"];
}

export const useHeatsForTimeRange = ({
  timeRange,
  day,
  prefetchRanges = [],
  filterMode,
}: UseHeatsForTimeRangeOptions) => {
  const tenantName = useTenant();

  const dispatch = useAppDispatch();

  const skipDegenerateRange = !day && timeRange.start === timeRange.end;

  const queryHookResult = useGetStrategyDashboardHeatsQuery(
    day
      ? { tenantName, start: "latest", filterMode }
      : { tenantName, start: timeRange.start, end: timeRange.end, filterMode },
    { skip: skipDegenerateRange }
  );

  useEffect(() => {
    if (prefetchRanges.length === 0) return;
    if (queryHookResult.isFetching) return;

    prefetchRanges.forEach((range) => {
      dispatch(
        generatedApi.util.prefetch(
          "getStrategyDashboardHeats",
          { start: range.start, end: range.end, tenantName, filterMode },
          { ifOlderThan: 60 }
        )
      );
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- filterMode changes should not re-trigger prefetch; prefetch only needs to run when the visible ranges change or the main query completes
  }, [prefetchRanges, dispatch, tenantName, queryHookResult.isFetching]);

  return queryHookResult;
};
